import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Baby, Plus, Pencil, Trash2, X, ShieldCheck, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Loader } from "@/components/Loader";

const EMPTY = { first_name: "", last_name: "", grade: "", allergies: [], favorite_meal_ids: [], school: "", school_name: "" };

const splitName = (full = "") => {
  const parts = full.trim().split(/\s+/);
  return { first_name: parts.shift() || "", last_name: parts.join(" ") };
};

export default function Children() {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [children, setChildren] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [meals, setMeals] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [schools, setSchools] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // child object or EMPTY
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [c, a, m, so] = await Promise.all([
      api.get("/children"),
      api.get("/allergens"),
      api.get("/meals?category=All"),
      api.get("/clover/school-options"),
    ]);
    const kids = c.data.children || [];
    setChildren(kids);
    setAllergens(a.data.allergens || []);
    setMeals((m.data.meals || []).filter((x) => !x.is_custom_box));
    setSchoolOptions(so.data.schools || []);
    // fetch each child's saved school (local layer)
    const entries = await Promise.all(
      kids.map((k) => api.get(`/clover/child-school/${k.child_id}`).then((r) => [k.child_id, r.data]).catch(() => [k.child_id, null]))
    );
    setSchools(Object.fromEntries(entries));
  };

  useEffect(() => {
    load()
      .catch(() => toast.error("Could not load kids."))
      .finally(() => setLoading(false));
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing({});
  };
  const openEdit = (c) => {
    const s = schools[c.child_id] || {};
    setForm({ ...splitName(c.name), grade: c.grade, allergies: c.allergies || [], favorite_meal_ids: c.favorite_meal_ids || [], school: s.school || "", school_name: s.school_name || "" });
    setEditing(c);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      toast.error("Please enter a first name.");
      return;
    }
    const name = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
    const payload = {
      name,
      grade: form.grade,
      allergies: form.allergies,
      favorite_meal_ids: form.favorite_meal_ids,
    };
    setSaving(true);
    try {
      let childId = editing.child_id;
      if (childId) {
        await api.patch(`/children/${childId}`, payload);
      } else {
        const res = await api.post("/children", payload);
        childId = res.data?.child?.child_id || res.data?.child_id || res.data?.id;
      }
      if (form.school && childId) {
        await api.post(`/clover/child-school/${childId}`, { school: form.school, school_name: form.school_name });
      }
      toast.success(editing.child_id ? "Child updated!" : "Child added! 🎉");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/children/${c.child_id}`);
      toast.success("Child removed.");
      await load();
    } catch (e) {
      toast.error("Could not delete.");
    }
  };

  const quickAddFavorites = (c) => {
    const favs = (c.favorite_meal_ids || []).map((id) => meals.find((m) => m.meal_id === id)).filter(Boolean);
    if (favs.length === 0) {
      toast.error("No favorites saved for this child.");
      return;
    }
    favs.forEach((m) => addItem(m, 1));
    toast.success(`Added ${favs.length} favorite${favs.length > 1 ? "s" : ""} to cart!`);
  };

  const shopSafe = (c) => {
    const params = new URLSearchParams();
    if (c.allergies?.length) params.set("hide", c.allergies.join(","));
    params.set("child", c.name);
    navigate(`/menu?${params.toString()}`);
  };

  const toggleAllergy = (a) =>
    setForm((f) => ({
      ...f,
      allergies: f.allergies.includes(a) ? f.allergies.filter((x) => x !== a) : [...f.allergies, a],
    }));

  const toggleFav = (id) =>
    setForm((f) => ({
      ...f,
      favorite_meal_ids: f.favorite_meal_ids.includes(id)
        ? f.favorite_meal_ids.filter((x) => x !== id)
        : [...f.favorite_meal_ids, id],
    }));

  if (loading) return <Loader label="Fetching your kids…" />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-800 tracking-tight flex items-center gap-3">
            <Baby className="text-purple-500" /> My Kids
          </h1>
          <p className="text-slate-500 mt-1">Manage allergies & favorites for safe, speedy ordering.</p>
        </div>
        <button
          data-testid="add-child-button"
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold rounded-2xl px-5 py-3 shadow-md shadow-emerald-200"
        >
          <Plus size={18} strokeWidth={3} /> Add Child
        </button>
      </div>

      {children.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border-2 border-dashed border-emerald-200">
          <div className="text-5xl mb-3">👨‍👩‍👧‍👦</div>
          <p className="text-slate-500 font-medium">No kids added yet. Add one to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {children.map((c) => (
            <div key={c.child_id} data-testid={`child-card-${c.child_id}`} className="bg-white rounded-3xl border-2 border-emerald-100/80 p-5">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h3 className="font-display font-bold text-xl text-slate-800">{c.name}</h3>
                  {c.grade && <p className="text-sm text-slate-500">Grade {c.grade}</p>}
                  {schools[c.child_id]?.label
                    ? <p data-testid={`child-school-${c.child_id}`} className="text-sm text-emerald-700 font-medium mt-0.5">🏫 {schools[c.child_id].label}</p>
                    : <p className="text-xs text-amber-600 mt-0.5">No school set</p>}
                </div>
                <div className="flex gap-1">
                  <button data-testid={`edit-child-${c.child_id}`} onClick={() => openEdit(c)} className="h-8 w-8 rounded-xl text-slate-500 hover:bg-emerald-50 flex items-center justify-center">
                    <Pencil size={16} />
                  </button>
                  <button data-testid={`delete-child-${c.child_id}`} onClick={() => remove(c)} className="h-8 w-8 rounded-xl text-rose-400 hover:bg-rose-50 flex items-center justify-center">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-xs font-semibold text-slate-400 uppercase mb-1.5">Allergies</p>
                {c.allergies?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {c.allergies.map((a) => (
                      <span key={a} className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                        {a}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">None 🎉</p>
                )}
              </div>

              <div className="flex flex-col gap-2 mt-5">
                <button
                  data-testid={`quick-add-favorites-${c.child_id}`}
                  onClick={() => quickAddFavorites(c)}
                  className="inline-flex items-center justify-center gap-2 bg-rose-400 hover:bg-rose-500 text-white font-semibold text-sm rounded-2xl py-2.5 transition-all"
                >
                  <Heart size={15} /> Quick Add Favorites ({c.favorite_meal_ids?.length || 0})
                </button>
                <button
                  data-testid={`shop-safe-${c.child_id}`}
                  onClick={() => shopSafe(c)}
                  className="inline-flex items-center justify-center gap-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold text-sm border-2 border-emerald-200 rounded-2xl py-2.5 transition-all"
                >
                  <ShieldCheck size={15} /> Shop Safe Menu
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form
            onSubmit={save}
            data-testid="child-form-modal"
            className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-2xl text-slate-800">
                {editing.child_id ? "Edit Child" : "Add Child"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600">
                <X size={22} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input
                data-testid="child-first-name-input"
                placeholder="First name"
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                className="px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-400 outline-none font-medium"
              />
              <input
                data-testid="child-last-name-input"
                placeholder="Last name"
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                className="px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-400 outline-none font-medium"
              />
              <input
                data-testid="child-grade-input"
                placeholder="Grade"
                value={form.grade}
                onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
                className="col-span-2 px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-400 outline-none font-medium"
              />
            </div>

            <div>
              <p className="font-display font-semibold text-slate-800 mb-2">School</p>
              <select
                data-testid="child-school-select"
                value={form.school}
                onChange={(e) => setForm((f) => ({ ...f, school: e.target.value }))}
                className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-400 outline-none font-medium bg-white"
              >
                <option value="">Select a school…</option>
                {schoolOptions.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
                <option value="other">Other</option>
              </select>
              {form.school === "other" && (
                <input
                  data-testid="child-school-name-input"
                  placeholder="Enter school name"
                  value={form.school_name}
                  onChange={(e) => setForm((f) => ({ ...f, school_name: e.target.value }))}
                  className="mt-2 w-full px-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-400 outline-none font-medium"
                />
              )}
            </div>

            <div>
              <p className="font-display font-semibold text-slate-800 mb-2">Allergies</p>
              <div className="flex flex-wrap gap-2">
                {allergens.map((a) => (
                  <button
                    key={a}
                    type="button"
                    data-testid={`child-allergy-${a.toLowerCase()}`}
                    onClick={() => toggleAllergy(a)}
                    className={`px-3.5 py-1.5 rounded-full font-semibold text-sm border-2 transition-all ${
                      form.allergies.includes(a) ? "bg-rose-400 text-white border-rose-400" : "bg-white text-slate-600 border-slate-200"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-display font-semibold text-slate-800 mb-2">Favorite meals</p>
              <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto">
                {meals.map((m) => (
                  <button
                    key={m.meal_id}
                    type="button"
                    data-testid={`child-fav-${m.meal_id}`}
                    onClick={() => toggleFav(m.meal_id)}
                    className={`flex items-center gap-2 p-2 rounded-2xl border-2 text-left transition-all ${
                      form.favorite_meal_ids.includes(m.meal_id) ? "border-emerald-400 bg-emerald-50" : "border-slate-200"
                    }`}
                  >
                    <img src={m.image_url} alt="" className="h-9 w-9 rounded-lg object-cover" />
                    <span className="text-sm font-medium text-slate-700 line-clamp-1">{m.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              data-testid="child-save-button"
              disabled={saving}
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-display font-bold rounded-2xl py-3.5 shadow-md shadow-emerald-200"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
